import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
    TrendingUp, 
    DollarSign, 
    Coins, 
    Activity, 
    ArrowUpRight, 
    Users, 
    Clock, 
    CheckCircle2,
    AlertCircle,
    Zap,
    RefreshCw
} from 'lucide-react';

const StatBox = ({ title, value, subtext, icon: Icon, color }) => (
    <div className="bg-zinc-900 border border-white/5 rounded-3xl p-8 relative overflow-hidden group">
        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
            <Icon size={80} />
        </div>
        <div className="flex items-start justify-between relative mb-4">
            <div className={`p-3 rounded-2xl`} style={{ backgroundColor: `${color}15`, border: `1px solid ${color}30` }}>
                <Icon size={20} style={{ color }} />
            </div>
            <div className="flex items-center gap-1 text-emerald-400 text-[10px] font-black uppercase tracking-widest">
                <ArrowUpRight size={12} />
                +2.4%
            </div>
        </div>
        <h3 className="text-zinc-500 font-black uppercase tracking-[0.2em] text-[10px] mb-1">{title}</h3>
        <p className="text-3xl font-black text-white italic">{value}</p>
        <p className="text-zinc-500 font-bold text-[9px] mt-2 uppercase tracking-widest">{subtext}</p>
    </div>
);

const FinancialDashboard = () => {
    const [stats, setStats] = useState({
        totalFiat: 0,
        totalEmotions: 0,
        activeArtistTills: 0,
        vpreBlockCount: 0,
        recentTransactions: []
    });
    const [loading, setLoading] = useState(true);
    const [alerts, setAlerts] = useState([]);

    // Manual trigger state
    const [viralTrackId, setViralTrackId] = useState('');
    const [payoutArtistId, setPayoutArtistId] = useState('');
    const [triggerStatus, setTriggerStatus] = useState('');  // '', 'loading', 'success', 'error'
    const [triggerMsg, setTriggerMsg] = useState('');

    useEffect(() => {
        const fetchFinancialData = async () => {
            try {
                // 1. Fetch VPRE Transactions (Fiat Flow)
                const vpreSnap = await getDocs(collection(db, 'vpre_transactions'));
                let fiatSum = 0;
                vpreSnap.forEach(doc => {
                    fiatSum += (doc.data().amountFiat || 0);
                });

                // 2. Fetch Treasury (eMôti∅n Flow)
                const eSnap = await getDocs(collection(db, 'label_treasury'));
                let emotionsSum = 0;
                eSnap.forEach(doc => {
                    if (doc.id === 'MAIN_eMOTIONS') emotionsSum = doc.data().balance || 0;
                });

                // 3. Fetch Artist Tills
                const artistSnap = await getDocs(query(collection(db, 'system_roles'), orderBy('role'), limit(50)));
                const artistCount = artistSnap.docs.filter(d => d.data().role === 'artist').length;

                // 4. Recent Activity
                const recentSnap = await getDocs(query(collection(db, 'vpre_transactions'), orderBy('timestamp', 'desc'), limit(5)));
                const recent = recentSnap.docs.map(d => ({ id: d.id, ...d.data() }));

                setStats({
                    totalFiat: fiatSum,
                    totalEmotions: emotionsSum,
                    activeArtistTills: artistCount,
                    vpreBlockCount: vpreSnap.size,
                    recentTransactions: recent
                });

                // Live operational alerts
                const alertsSnap = await getDocs(
                    query(collection(db, 'operational_alerts'), orderBy('timestamp', 'desc'), limit(5))
                );
                setAlerts(alertsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            } catch (error) {
                console.error('Financial Data Fetch Error:', error);
            }
            setLoading(false);
        };

        fetchFinancialData();
    }, []);

    const handleViralReward = async () => {
        if (!viralTrackId.trim()) return;
        setTriggerStatus('loading');
        setTriggerMsg('');
        try {
            const rewardCosigners = httpsCallable(getFunctions(), 'rewardCosigners');
            const res = await rewardCosigners({ trackId: viralTrackId.trim() });
            setTriggerMsg(`✅ ${res.data.cosignersRewarded} cosigners rewarded ${res.data.miviPerCosigner} MIVI each.`);
            setTriggerStatus('success');
        } catch (e) {
            setTriggerMsg(`❌ ${e.message}`);
            setTriggerStatus('error');
        }
    };

    const handleArtistPayout = async () => {
        if (!payoutArtistId.trim()) return;
        setTriggerStatus('loading');
        setTriggerMsg('');
        try {
            const payoutArtist = httpsCallable(getFunctions(), 'payoutArtist');
            const res = await payoutArtist({ artistId: payoutArtistId.trim() });
            setTriggerMsg(`✅ KES ${res.data.amountKES} dispatched. ${res.data.attributionCount} attributions cleared.`);
            setTriggerStatus('success');
        } catch (e) {
            setTriggerMsg(`❌ ${e.message}`);
            setTriggerStatus('error');
        }
    };

    if (loading) return <div className="text-zinc-500 font-mono text-xs animate-pulse">Scanning Ecosystem Treasury...</div>;

    return (
        <div className="space-y-12 pb-24">
            <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <h1 className="text-5xl font-black text-white tracking-tighter uppercase italic leading-none mb-4">Financial Ecosystem</h1>
                    <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 text-[10px] font-black uppercase tracking-widest rounded-full border border-emerald-500/20">
                            Real-Time Ledger Active
                        </span>
                        <span className="text-zinc-500 font-bold uppercase tracking-widest text-[9px]">Consolidated Settlement Overview</span>
                    </div>
                </div>
                <div className="flex items-center gap-4 bg-zinc-900 p-4 rounded-2xl border border-white/5">
                    <Activity size={18} className="text-brand-accent animate-pulse" />
                    <div>
                        <p className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">Global PONT Index</p>
                        <p className="text-sm font-black text-white italic">#{stats.vpreBlockCount.toString().padStart(6, '0')}</p>
                    </div>
                </div>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatBox 
                    title="Total Fiat Revenue" 
                    value={`KES ${stats.totalFiat.toLocaleString()}`} 
                    subtext="Real-world cash flow through VPRE"
                    icon={DollarSign}
                    color="#00d2ff"
                />
                <StatBox 
                    title="eMôti∅n Supply" 
                    value={stats.totalEmotions.toLocaleString()} 
                    subtext="Internal interaction currency"
                    icon={Coins}
                    color="#ff0055"
                />
                <StatBox 
                    title="Verified Arist Tills" 
                    value={stats.activeArtistTills} 
                    subtext="Active M-Pesa endpoints"
                    icon={TrendingUp}
                    color="#9d50bb"
                />
                <StatBox 
                    title="Blockchain Blocks" 
                    value={stats.vpreBlockCount} 
                    subtext="Total immutable notarizations"
                    icon={CheckCircle2}
                    color="#fbbf24"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* RECENT SETTLEMENTS */}
                <div className="lg:col-span-2 bg-zinc-900/50 border border-white/5 rounded-3xl p-8 shadow-inner">
                    <div className="flex items-center justify-between mb-8">
                        <h2 className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-2">
                            <Clock size={16} className="text-zinc-500" /> Recent Ledger Settlements
                        </h2>
                        <button className="text-[10px] font-black text-zinc-500 hover:text-white uppercase tracking-widest transition-colors">View All Archive</button>
                    </div>

                    <div className="space-y-4">
                        {stats.recentTransactions.map((tx, i) => (
                            <div key={tx.id} className="group bg-black/40 border border-transparent hover:border-white/10 p-5 rounded-2xl transition-all flex items-center gap-6">
                                <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center shrink-0 group-hover:bg-brand-primary/10 transition-colors">
                                    <span className="text-xs font-black text-zinc-500 group-hover:text-brand-primary">#{tx.index}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-black text-white truncate uppercase tracking-tighter">{tx.type} — {tx.sourceUserId}</p>
                                    <p className="text-[9px] text-zinc-500 font-bold uppercase tracking-widest mt-1">Receipt ID: {tx.receiptRef || 'UNNOTARIZED_GHOST'}</p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-xs font-black text-emerald-400 italic">KES {tx.amountFiat}</p>
                                    <p className="text-[8px] text-zinc-600 font-mono mt-1">{new Date(tx.timestamp).toLocaleTimeString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* TILL MONITOR */}
                <div className="bg-zinc-900 border border-white/5 rounded-3xl p-8 flex flex-col">
                    <h2 className="text-sm font-black text-white uppercase tracking-widest mb-6 flex items-center gap-2">
                        <AlertCircle size={16} className="text-orange-400" /> Operational Alerts
                    </h2>
                    
                    <div className="flex-1 space-y-3">
                        {alerts.length === 0 ? (
                            <div className="p-5 bg-emerald-400/5 border border-emerald-400/20 rounded-2xl">
                                <p className="text-[9px] font-black text-emerald-400 uppercase tracking-widest mb-1">All Systems Nominal</p>
                                <p className="text-xs text-zinc-100 leading-relaxed font-bold">No active alerts. Ledger is healthy.</p>
                            </div>
                        ) : alerts.map(alert => (
                            <div key={alert.id} className={`p-4 rounded-2xl border ${
                                alert.type?.includes('FAILURE') || alert.type?.includes('TIMEOUT')
                                    ? 'bg-red-400/5 border-red-400/20'
                                    : 'bg-orange-400/5 border-orange-400/20'
                            }`}>
                                <p className={`text-[9px] font-black uppercase tracking-widest mb-1 ${
                                    alert.type?.includes('FAILURE') || alert.type?.includes('TIMEOUT') ? 'text-red-400' : 'text-orange-400'
                                }`}>{(alert.type || 'ALERT').replace(/_/g, ' ')}</p>
                                <p className="text-xs text-zinc-100 leading-relaxed font-bold">
                                    {alert.message || alert.resultDesc || 'Review required.'}
                                </p>
                                <p className="text-[8px] text-zinc-500 mt-1">{new Date(alert.timestamp).toLocaleString()}</p>
                            </div>
                        ))}
                    </div>

                    <p className="mt-8 text-[9px] text-zinc-500 font-bold leading-relaxed uppercase tracking-widest text-center italic">
                        All financial attributions are finalized via VPRE Protocol.
                    </p>
                </div>
            </div>

            {/* MANUAL ENGINE CONTROLS */}
            <div className="bg-zinc-900 border border-white/5 rounded-3xl p-8">
                <h2 className="text-sm font-black text-white uppercase tracking-widest mb-2 flex items-center gap-2">
                    <Zap size={16} className="text-yellow-400" /> Manual Engine Controls
                </h2>
                <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-8">Manager-only. These triggers fire Cloud Functions directly.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* COSIGN VIRAL REWARD */}
                    <div className="space-y-3 p-6 bg-black/40 rounded-2xl border border-white/5">
                        <p className="text-xs font-black text-white uppercase tracking-widest">Cosign Viral Reward</p>
                        <p className="text-[10px] text-zinc-500">Awards 100 MIVI to all cosigners of a track that crossed 10k plays.</p>
                        <input
                            value={viralTrackId}
                            onChange={e => setViralTrackId(e.target.value)}
                            placeholder="Track / Project ID"
                            className="w-full bg-zinc-900 border border-white/10 rounded-xl p-3 text-white text-xs font-mono"
                        />
                        <button
                            onClick={handleViralReward}
                            disabled={triggerStatus === 'loading' || !viralTrackId.trim()}
                            className="w-full py-3 bg-yellow-400 text-black font-black text-xs uppercase tracking-widest rounded-xl hover:bg-yellow-300 disabled:opacity-40 flex items-center justify-center gap-2"
                        >
                            <Zap size={14} />
                            {triggerStatus === 'loading' ? 'Processing...' : 'Trigger Viral Reward'}
                        </button>
                    </div>

                    {/* ARTIST PAYOUT */}
                    <div className="space-y-3 p-6 bg-black/40 rounded-2xl border border-white/5">
                        <p className="text-xs font-black text-white uppercase tracking-widest">Artist B2C Payout</p>
                        <p className="text-[10px] text-zinc-500">Disperses all pending tip attributions to the artist's registered M-Pesa till.</p>
                        <input
                            value={payoutArtistId}
                            onChange={e => setPayoutArtistId(e.target.value)}
                            placeholder="Artist UID"
                            className="w-full bg-zinc-900 border border-white/10 rounded-xl p-3 text-white text-xs font-mono"
                        />
                        <button
                            onClick={handleArtistPayout}
                            disabled={triggerStatus === 'loading' || !payoutArtistId.trim()}
                            className="w-full py-3 bg-white text-black font-black text-xs uppercase tracking-widest rounded-xl hover:bg-zinc-200 disabled:opacity-40 flex items-center justify-center gap-2"
                        >
                            <RefreshCw size={14} />
                            {triggerStatus === 'loading' ? 'Processing...' : 'Dispatch Payout'}
                        </button>
                    </div>
                </div>

                {triggerMsg && (
                    <p className={`mt-6 text-xs font-bold text-center ${
                        triggerStatus === 'success' ? 'text-emerald-400' : 'text-red-400'
                    }`}>{triggerMsg}</p>
                )}
            </div>
        </div>
    );
};

export default FinancialDashboard;
